/**
 * GA4 Analytics — consent-gated.
 *
 * The gtag library is loaded only after the user clicks "Accept All" in the
 * CookieConsentBanner. Before that, every track call is a no-op so nothing
 * leaves the browser.
 */

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const CONSENT_KEY = 'portico_cookie_consent';

let initialized = false;

/** @returns {boolean} true if user has accepted cookies */
export function hasConsent() {
  return typeof window !== 'undefined' && localStorage.getItem(CONSENT_KEY) === 'accepted';
}

/**
 * Inject the gtag.js script and configure GA4. Idempotent — safe to call
 * multiple times.
 */
export function initGA() {
  if (initialized || !GA_ID || typeof window === 'undefined') return;
  if (!hasConsent()) return;

  // Load library
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer + gtag
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag('js', new Date());
  // anonymize_ip = privacy-friendly default
  // send_page_view = false because we manually fire pageviews on route change
  gtag('config', GA_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  initialized = true;

  // Fire the initial pageview now that GA is up
  trackPageview(window.location.pathname + window.location.search);
}

/**
 * Fire a virtual pageview. Safe to call before consent — silently dropped.
 */
export function trackPageview(path) {
  if (!initialized || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/**
 * Fire a custom conversion event.
 *
 * @param {string} name  - event name (e.g. 'quote_submitted')
 * @param {object} [params] - optional event parameters
 */
export function trackEvent(name, params = {}) {
  if (!initialized || !window.gtag) return;
  window.gtag('event', name, params);
}
